import { useState } from "react";
import { PrimaryBtn } from "../components/Buttons";
import Screen from "../components/Screen";
import { useSupplier } from "../SupplierContext";
import { useKiosk } from "@screen/Kiosk/useKiosk";

interface SupplierPhoneProps {
  /** 前一步查不到的門號（顯示提示用） */
  triedPhone?: string;
  /**
   * 「我是這間瓦斯行的客戶」＋有填電話 → 把填的電話（可多組）交給上層打真後端
   * match/by-landline 比對（2026-07-15 接真化：原本用 demo 名單本地比對，真號碼永遠不中）。
   */
  onCheckExisting: (phones: string[]) => void;
  /** 自述是這間瓦斯行的客戶，但電話沒填 → 仍以既有身分繼續（待核對） */
  onExisting: () => void;
  /** 不是這間瓦斯行的客戶（第一次叫）→ 以新客戶繼續（鋼瓶/用途留到結帳） */
  onNew: () => void;
  /** 省略＝這一頁沒有上一步（流程第一頁）→ 不顯示返回鍵。由 Onboarding 的導航堆疊 canGoBack 決定。 */
  onBack?: () => void;
  /** 上層比對進行中（禁止重複送出） */
  checking?: boolean;
}

/**
 * 「輸入家用電話」＋「是不是這間瓦斯行客戶」二選一。
 * 客戶登記在瓦斯行的電話可能不只一組（市話 / 另一支手機 / 家人的），可填多組一起查；
 * 填了就用來比對既有資料，也可直接以下方按鈕表明身分。
 */
const SupplierPhone = ({ triedPhone, onCheckExisting, onExisting, onNew, onBack, checking }: SupplierPhoneProps) => {
  /** 哪一顆次要按鈕正在送出（供該顆顯示轉圈）；null = 沒有。 */
  const [submitting, setSubmitting] = useState<null | "existing" | "new">(null);
  const supplier = useSupplier();
  const { setEntryLandline } = useKiosk();
  const [phones, setPhones] = useState<string[]>([""]);

  const update = (i: number, v: string) => {
    const n = [...phones];
    n[i] = v;
    setPhones(n);
  };
  const add = () => setPhones([...phones, ""]);
  const remove = (i: number) => setPhones(phones.filter((_, idx) => idx !== i));

  const cleaned = phones.map((p) => p.replace(/\D/g, "")).filter(Boolean);
  const canSubmit = cleaned.length > 0;

  // 有填寫任何電話都先記下（供後續 walk-in body 帶 landline，讓後端 cascade 比對用）
  const captureLandline = () => {
    if (cleaned.length) setEntryLandline(cleaned.join(","));
  };

  // 「我是這間瓦斯行的客戶」（需有填電話）：交上層打真後端 match/by-landline 逐組比對；
  // 比對到 → 確認卡；全部沒中 → 以既有(待核對)繼續（由上層決定）。
  const chooseExisting = () => {
    if (checking || submitting) return;
    captureLandline();
    onCheckExisting(cleaned);
  };

  // 三顆按鈕的收尾都會打 API（比對 / ensureRegistered 建 customer），過去只有第一顆有
  // checking，另外兩顆既無 loading 也不禁點 → 可連點造成重複註冊。改為共用一個 busy：
  // 任一顆按下就鎖住三顆，並在該顆顯示轉圈。
  const busy = !!checking || submitting !== null;

  const handleExisting = () => {
    if (busy) return;
    setSubmitting("existing");
    captureLandline();
    onExisting();
  };

  const handleNew = () => {
    if (busy) return;
    setSubmitting("new");
    captureLandline();
    onNew();
  };

  return (
    <Screen
      title="您是這間瓦斯行的客戶嗎？"
      subtitle={triedPhone ? `找不到「${triedPhone}」，可再填其他電話，或直接選擇下方身分。` : "可填家用電話，或直接選擇下方身分。"}
      onBack={onBack}
      footer={
        <>
          <PrimaryBtn active={canSubmit} busy={checking} busyText="確認中…" onClick={chooseExisting}>
            {/* 名稱來自 shopCode 解析；查不到時只有 supplier_id、沒有名字 → 不要留出「我是  的客戶」的空格。 */}
            {supplier.name ? `我是 ${supplier.name} 的客戶` : "我是這間瓦斯行的客戶"}
          </PrimaryBtn>
          <div className="h-px bg-[#E7ECF3] my-2" />
          {/* 次要外框按鈕（較低調）：非主要路徑，弱化樣式避免與主按鈕爭焦點。
              onClick 用 handleExisting/handleNew（會先 captureLandline，供 walk-in cascade 帶市話）。 */}
          <button
            onClick={handleExisting}
            disabled={busy}
            className={`w-full py-3 rounded-[14px] border border-[#E7ECF3] bg-white text-[#64748B] text-[14px] font-medium flex items-center justify-center gap-2 ${busy ? "cursor-default opacity-60" : "cursor-pointer"}`}
          >
            {submitting === "existing" && (
              <span className="inline-block w-[15px] h-[15px] border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {submitting === "existing" ? "處理中…" : "忘記 / 沒有登記電話，但我是這間客戶"}
          </button>
          <button
            onClick={handleNew}
            disabled={busy}
            className={`w-full py-3 rounded-[14px] border border-[#E7ECF3] bg-white text-[#64748B] text-[14px] font-medium flex items-center justify-center gap-2 ${busy ? "cursor-default opacity-60" : "cursor-pointer"}`}
          >
            {submitting === "new" && (
              <span className="inline-block w-[15px] h-[15px] border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {submitting === "new" ? "處理中…" : "不是，我第一次叫這家瓦斯"}
          </button>
        </>
      }
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[13px] text-[#475569] font-semibold">家用電話</span>
        <span className="text-[12px] text-[#2196f3] font-extrabold bg-[#EFF6FF] border border-[#BFDBFE] rounded-full px-2.5 py-1">選填</span>
      </div>
      <div className="text-[12px] text-[#94A3B8] mb-2.5">可填多組（市話 / 另一支手機 / 家人的）；不填也可直接按下方「我是…客戶」。</div>
      <div className="flex flex-col gap-2.5">
        {phones.map((p, i) => (
          <div key={i} className="flex gap-2.5 items-center">
            <input
              value={p}
              onChange={(e) => update(i, e.target.value)}
              inputMode="tel"
              placeholder="09xx xxx xxx 或 04-xxxx-xxxx"
              className="flex-1 h-[54px] px-4 border-none rounded-[14px] bg-[#F1F5F9] text-[16px] outline-none box-border text-[#333]"
            />
            {phones.length > 1 && (
              <button
                onClick={() => remove(i)}
                className="w-9 h-9 rounded-full bg-[#F1F5F9] text-[#94A3B8] text-lg shrink-0 cursor-pointer border-none"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      <button onClick={add} className="mt-2.5 text-[13px] font-semibold text-[#2196f3] bg-transparent border-none cursor-pointer p-0">
        ＋ 再填一組電話
      </button>
    </Screen>
  );
};

export default SupplierPhone;
